/**
 * Skill — el banco (skill.html): ver y editar SON la misma pantalla; en M1
 * solo consulta (la curación escribe por PR hasta F5b). REAL: versiones,
 * método por versión, diff v1→v2 calculado y el probador ejecutando la
 * regla de producción — y la TELEMETRÍA, que desde `/studio/skills/
 * telemetria` sale de las trazas persistidas (ya no se siembra).
 * YA NO HAY SEMILLA en el banco: lo que la API no da —fechas de versión,
 * fuentes, insight de curación, actividad, rastro, desglose por día o por
 * casa, conflictos de precedencia— se enseña como VACÍO HONESTO marcado
 * «fase dev». «—» significa «no se mide», nunca cero, y el porqué va en
 * prosa VISIBLE bajo los números (el tooltip solo repite; no sustituye).
 * Escrituras → toast honesto.
 */
import { useState } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Enrutado } from "@skein/contracts";
import { ApiError } from "@skein/api-client";
import { Badge, Banner, BtnGroup, Button, Card, CardHeader, Delta, Empty, FmRow, Help, Hint, Input, MethodDiff, MethodView, Mono, PageHead, Skeleton, Stale, StatCell, StatGrid, Tab, Tabs, Tip, TraceRow, VTimeline, VersionCard, buttonClass, diffLines, useToast } from "@skein/ui";
import { studio } from "../../api/client";
import { retryEvery30s, useSkillsTelemetria } from "../../api/queries";
import { deltaTexto, deltaTono, diasVentana, filaDe, indexar, ventana } from "./telemetria";
import { fechaTabla } from "../implantaciones/fecha";

const F5B = "La curación escribe con F5b — de momento se edita content/skills por PR.";
const SIN_RESUELTAS = "sin propuestas resueltas todavía";
const SIN_CONFLICTOS = "aún no se mide: la traza no guarda qué otros métodos casaron";
const CAPAS = ["general", "ceo", "cfo", "production", "commercial"] as const;

function Cargando() {
  const ln = (w: string, extra?: React.CSSProperties) => <Skeleton style={{ height: ".9em", width: w, ...extra }} />;
  const gap = { marginBottom: "var(--sk-sp-3)" };
  return (
    <section aria-busy="true" aria-label="Cargando el skill">
      <PageHead title="" skeleton />
      {/* la MISMA retícula que el detalle: si no, el esqueleto prefigura dos
          columnas y al llegar los datos la pantalla salta a pila */}
      <div className="cols cols--rail">
        <div className="stack">
          <Card><Skeleton style={{ height: "2.5rem" }} /></Card>
          <Card>{ln("70%", gap)}{ln("100%")}{ln("85%", { marginTop: "var(--sk-sp-2)" })}{ln("60%", { marginTop: "var(--sk-sp-2)" })}</Card>
          <Card>{ln("40%", gap)}<Skeleton style={{ height: "2.5rem" }} /></Card>
        </div>
        <div className="stack">
          <Card>{ln("50%", gap)}<Skeleton style={{ height: "3.5rem" }} /></Card>
          <Card>{ln("55%", gap)}{ln("100%")}{ln("70%", { marginTop: "var(--sk-sp-2)" })}</Card>
          <Card>{ln("45%", gap)}{ln("100%")}{ln("75%", { marginTop: "var(--sk-sp-2)" })}</Card>
        </div>
      </div>
    </section>
  );
}

function Probador({ disabled }: { disabled: boolean }) {
  const [frase, setFrase] = useState("");
  const [out, setOut] = useState<Enrutado | null>(null);
  const [fallo, setFallo] = useState(false);
  const [busy, setBusy] = useState(false);
  const trazar = () => {
    if (!frase.trim()) { setOut(null); setFallo(false); return; }
    setBusy(true);
    // si la red falla, se dice — y la traza anterior no se descarta
    studio.enrutado(frase).then((r) => { setOut(r); setFallo(false); }).catch(() => setFallo(true)).finally(() => setBusy(false));
  };
  return (
    <Card flush aria-label="Probador de enrutado">
      <CardHeader title="Probador de enrutado" hint="la traza que verá auditoría — la regla propone candidatos y el modelo elige" />
      <div className="probe-in">
        <Input mono placeholder="Escribe como escribiría un directivo… p. ej. «el margen ha caído y la tesorería está tensa»" aria-label="Frase de prueba"
          value={frase} onChange={(e) => setFrase(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") trazar(); }} readOnly={disabled} />
        <Button onClick={trazar} loading={busy} disabled={disabled}>Trazar</Button>
      </div>
      <div className="trace-out">
        {fallo && <Empty inline title="No llego al probador.">La regla corre en el servidor y no responde. La traza de abajo, si la hay, es la anterior. <Button variant="link" onClick={trazar}>reintentar ahora</Button></Empty>}
        {!out && !fallo && <Empty inline title="Sin frase aún.">El probador ejecuta la misma regla de precedencia que producción y pinta la traza completa: qué palabras casan y cuánto pesan, qué frases disparan, qué capa aplica y qué candidatos se le darían al modelo para elegir. Lo que no se puede explicar aquí, no se despliega.</Empty>}
        {out && out.rows.map((r) => <TraceRow key={r.slug} slug={r.slug} keywords={r.keywords} capa={r.capa} score={r.score} win={(out.candidatos ?? []).includes(r.slug)} />)}
        {out && !fallo && ((out.candidatos ?? []).length > 0
          ? <Empty inline solid title={`Candidatos: ${(out.candidatos ?? []).join(", ")}.`}>{out.nota ?? "Elegiría el modelo entre ellos."} El Advisor recibirá el método elegido + los criterios confirmados de la casa (R-4). Esta misma traza queda en auditoría.</Empty>
          : <Empty inline title="Sin candidatos.">{out.nota ?? "El turno no trae ninguna palabra rara ni frase."} Sin método anterior, el asesor responde desde conocimiento general con degradación explícita (R-4). Nunca bloquea (R-1).</Empty>)}
      </div>
    </Card>
  );
}

export function SkillPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const search = useSearch({ strict: false }) as { estado?: string };
  const say = useToast();
  const q = useQuery({ queryKey: ["skill", slug], queryFn: () => studio.skill(slug), ...retryEvery30s });
  const tel = useSkillsTelemetria();
  const [tab, setTab] = useState<"metodo" | "front" | "diff">("metodo");

  const notFound = q.error instanceof ApiError && q.error.status === 404;
  const forced = search.estado;
  const view = forced === "cargando" || forced === "error" ? forced
    : q.isPending ? "cargando" : notFound ? "no-existe" : q.isError && !q.data ? "error-sin-datos" : q.isError ? "error" : "detalle";
  if (view === "cargando") return <Cargando />;
  if (view === "no-existe") return (
    <Empty glyph="✳" title={`No hay skill «${slug}» en el catálogo`}>
      Quizá se retiró o el enlace es viejo.
      <Link to="/skills" className={buttonClass({ variant: "primary" })}>Volver a la lista</Link>
    </Empty>
  );
  if (view === "error-sin-datos" || !q.data) return (
    <Banner tone="danger"><b>No llego al catálogo.</b> Y aún no tengo el skill en memoria. Reintento solo cada 30 s. <Button variant="link" onClick={() => void q.refetch()}>reintentar ahora</Button></Banner>
  );

  const s = q.data;
  const enError = view === "error";
  // la telemetría va por su cuenta: el banco se lee aunque el motor de trazas calle
  const telView = forced === "cargando" ? "cargando" : forced === "error" ? "error"
    : tel.isPending ? "cargando" : tel.isError && !tel.data ? "error" : "datos";
  const tdata = telView === "datos" ? tel.data : undefined;
  const t = filaDe(indexar(tdata), slug);
  const dt = deltaTexto(t.delta);
  const dv = diasVentana(tdata);
  /** cifra de la telemetría: esqueleto mientras carga, «—» si no llega. */
  const cifra = (v: React.ReactNode, w = "3rem") =>
    telView === "cargando" ? <Skeleton style={{ width: w, height: "1.6em" }} /> : telView === "error" ? "—" : v;
  const pub = s.versiones.find((v) => v.estado === "published");
  const draft = [...s.versiones].reverse().find((v) => v.estado === "draft");
  const metodoActual = (draft && s.metodos[String(draft.version)]) || (pub && s.metodos[String(pub.version)]) || "";
  const dif = pub && draft ? diffLines(s.metodos[String(pub.version)] ?? "", s.metodos[String(draft.version)] ?? "") : [];
  const nAdd = dif.filter((l) => l.kind === "add").length, nDel = dif.filter((l) => l.kind === "del").length;
  const rito = (que: string) => say(`${que} exige su rito (consecuencias a la vista y mantener pulsado) y llega con F5b.`);

  return (
    <>
      {enError && (
        <Banner tone="danger" style={{ marginBottom: "var(--sk-gap)" }}><b>No llego al catálogo.</b> Lo que ves puede estar desactualizado y no se puede editar ni publicar. Reintento solo cada 30 s. <Button variant="link" onClick={() => void q.refetch()}>reintentar ahora</Button></Banner>
      )}
      <Stale stale={enError}>
        <PageHead
          title={s.nombre}
          meta={<>
            {pub && <Badge tone="ok">{`v${pub.version} · published`}</Badge>}
            {draft && <Badge tone="accent">{`v${draft.version} · draft — editando`}</Badge>}
            <Badge>capa · {s.capa}</Badge>
            {tdata && <Badge>{t.casas} implantaciones · {ventana(tdata)}</Badge>}
          </>}
          actions={<>
            <Button variant="danger" disabled={enError} onClick={() => rito("Retirar")}>Retirar skill</Button>
            <Button disabled={enError} onClick={() => say("El sandbox llega con F5b.")}>Probar en sandbox</Button>
            {draft && <Button variant="primary" disabled={enError} onClick={() => rito(`Publicar v${draft.version}`)}>Publicar v{draft.version}…</Button>}
          </>}
        />
        <div className="cols cols--rail">
          <div className="stack">
            <Card flush aria-label="Versiones">
              <VTimeline>
                {pub && <VersionCard state="pub" v={`v${pub.version}`} s="published — la que razona hoy" d="historial en git · fecha y autor, fase dev" />}
                {draft && <VersionCard state="draft" v={`v${draft.version}`} s="draft — sin publicar" d="en curso" />}
                <VersionCard state="ghost" v={`v${(draft?.version ?? pub?.version ?? 0) + 1}`} s={<Button variant="link" disabled={enError} onClick={() => say(F5B)}>+ nueva versión desde v{draft?.version ?? pub?.version ?? 1}</Button>} />
              </VTimeline>
            </Card>

            <Card flush aria-label={draft ? `Versión v${draft.version} (draft)` : "Método publicado"}>
              <Tabs style={{ padding: "0 var(--sk-sp-3)" }}>
                <Tab selected={tab === "metodo"} onClick={() => setTab("metodo")} mod="M">Método</Tab>
                <Tab selected={tab === "front"} onClick={() => setTab("front")}>Frontmatter</Tab>
                {pub && draft && <Tab selected={tab === "diff"} onClick={() => setTab("diff")} mod={`+${nAdd} −${nDel}`}>{`Diff v${pub.version} → v${draft.version}`}</Tab>}
              </Tabs>
              {tab === "metodo" && (
                <div role="tabpanel">
                  <div style={{ display: "flex", justifyContent: "flex-end", padding: "var(--sk-sp-3) var(--sk-card-pad-x) 0" }}>
                    <Button variant="quiet" size="s" disabled={enError} onClick={() => say(F5B)}>Editar el método</Button>
                  </div>
                  <MethodView text={metodoActual} />
                </div>
              )}
              {tab === "front" && (
                <div role="tabpanel">
                  <FmRow label="slug"><Input mono value={s.slug} readOnly /></FmRow>
                  <FmRow label="topic"><Input value={s.tema} readOnly /></FmRow>
                  <FmRow label="capa / roles"><BtnGroup label="Capa del skill" options={CAPAS} pressed={new Set(s.capa === "general" ? ["general"] : s.capa.split(","))} readOnlyReason={F5B} /></FmRow>
                  <FmRow label={draft ? `estado v${draft.version}` : "estado"}><div>{draft ? <Badge tone="accent">draft</Badge> : <Badge tone="ok">published</Badge>}</div></FmRow>
                  <FmRow label="fuentes"><div><Hint>fase dev — el adapter aún no expone de qué fuentes bebe el método</Hint></div></FmRow>
                </div>
              )}
              {tab === "diff" && pub && draft && (
                <div role="tabpanel"><MethodDiff a={s.metodos[String(pub.version)] ?? ""} b={s.metodos[String(draft.version)] ?? ""} /></div>
              )}
            </Card>

            <Probador disabled={enError} />
          </div>

          <div className="stack">
            <Card flush aria-label="Telemetría del skill" aria-describedby="tel-glosa">
              {/* el hint dice QUÉ VERSIÓN produce estos números (contrato de skill.html);
                  el «último uso» va abajo en prosa, que cabe y se lee en cualquier ancho */}
              <CardHeader title={dv === null ? "Comportamiento" : `Comportamiento · ${dv}d`}
                hint={pub ? `v${pub.version} en producción` : "sin versión publicada"} />
              <StatGrid>
                <StatCell n={cifra(<>{t.disparos}{dt !== null && t.delta !== null && <Delta tone={deltaTono(t.delta)}>{dt}</Delta>}</>)} label="disparos" />
                <StatCell n={cifra(t.confirmadasPct !== null ? `${t.confirmadasPct}%` : <Tip tip={SIN_RESUELTAS}>—</Tip>)}
                  label={t.propuestas > 0 ? `confirmadas de ${t.propuestas} propuestas` : "propuestas confirmadas"} />
                <StatCell n={<Tip tip={SIN_CONFLICTOS}>—</Tip>} label="conflictos de precedencia" />
              </StatGrid>
              {telView === "error"
                ? <Empty inline title="No llego a la telemetría." style={{ margin: "0 var(--sk-card-pad-x) var(--sk-sp-2)" }}>El método y sus versiones sí son reales; los números de uso, no los tengo. Reintento solo cada 30 s. <Button variant="link" onClick={() => void tel.refetch()}>reintentar ahora</Button></Empty>
                : <Empty inline title="Sin desglose por día ni por casa." style={{ margin: "0 var(--sk-card-pad-x) var(--sk-sp-2)" }}>
                    La ventana se agrega entera: la serie diaria y el reparto por implantación llegan con la fase dev de la telemetría — no se siembran.
                    {tdata && (t.ultimoUso ? <> Último uso {fechaTabla(t.ultimoUso, false)}.</> : <> Sin un solo disparo {dv === null ? "en la ventana" : `en los últimos ${dv} días`}.</>)}
                  </Empty>}
              {/* EL PORQUÉ EN PROSA, no solo en el tooltip: el tooltip del sistema
                  cuelga de un span que no enfoca con teclado, y se apaga por
                  debajo de 560 px. Aquí el tooltip repite; no sustituye. */}
              <Help id="tel-glosa" style={{ margin: "0 var(--sk-card-pad-x) var(--sk-card-pad-y)" }}>
                El chip junto a los disparos es la variación frente a {dv === null ? "la ventana anterior" : `los ${dv} días anteriores`} —verde sube, rojo baja—: dirección, no juicio.
                «—» marca lo que <b>no se mide</b>, nunca un cero: los conflictos de precedencia todavía no se miden (la traza no guarda qué OTROS métodos casaron) y «confirmadas» calla mientras ninguna propuesta se haya resuelto.
              </Help>
            </Card>

            <Card flush aria-label="Actividad">
              <CardHeader title="Actividad" hint="fase dev — solo añade, nunca reescribe" />
              <Empty inline title="Sin rastro todavía." style={{ margin: "var(--sk-sp-3) var(--sk-card-pad-x)" }}>
                Quién curó qué y cuándo se escribirá en el registro de auditoría cuando tenga su endpoint (fase dev). Hasta entonces esta columna va vacía: sembrarla con nombres y horas inventadas sería mentir sobre lo único que aquí importa, el rastro.
              </Empty>
            </Card>
          </div>
        </div>
      </Stale>
    </>
  );
}
